import React, { useState } from 'react';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import {
  DropdownFilter,
  FilterArrayType,
  FilterElement,
} from 'common/components/Filters';
import { Stack, ChoiceGroup } from '@fluentui/react';
import { useStyles } from './index.styles';
import { useQuery, useReactiveVar } from '@apollo/client';
import { PayCycleCommonTypes } from 'ap/paymentCycle/__generated__/PayCycleCommonTypes';
import COMMON_DATA from '../PayCycleCommonTypes.graphql';
import { formatDropdownOptions } from 'common/utils';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import { payCycleMode, PayCycleView } from 'ap/paymentCycle';
import { globalMode } from 'utility/cache/ui';
import { choiceGroupStyles, options } from 'common/utils/listViewOptionStyle';

interface PayCycleCommonSearchBarProps extends SearchBarProps {
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
}

export const PayCycleCommonSearchBar: React.FC<
  PayCycleCommonSearchBarProps
> = ({
  columns,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  urlParams,
  onFilterChange,
}) => {
  const styles = useStyles();
  const globalState = useReactiveVar(globalMode);
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const { data: commonData } = useQuery<PayCycleCommonTypes>(COMMON_DATA);
  const transactionTypeOptions = formatDropdownOptions(
    commonData?.paymentTransactionTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.transactionType!,
      includeAll: true,
    }
  );

  const accountOptions = formatDropdownOptions(
    commonData?.paymentAccounts?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );

  const statusTypeOptions = formatDropdownOptions(
    commonData?.invoiceStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
      includeAll: true,
    }
  );

  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  const mode = useReactiveVar(payCycleMode);

  const filters: FilterElement[] = [
    <DropdownFilter
      filterKey={'transactionTypeId'}
      options={transactionTypeOptions}
      placeholder={'Transaction type'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'paymentAccountId'}
      options={accountOptions}
      placeholder={'Bank account'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'_statusTypeId'}
      options={statusTypeOptions}
      placeholder={'Status'}
      onOptionChange={onOptionChange}
    />,
  ];

  return (
    <SearchBar
      onFilterClear={() => {
        setFilterTypes([]);
        onFilterChange([]);
      }}
      urlParams={urlParams}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <ChoiceGroup
            options={options}
            disabled={globalState}
            selectedKey={mode}
            styles={choiceGroupStyles}
            onChange={(_, option) => {
              if (!option) return;
              payCycleMode(option.key as PayCycleView);
            }}
          />
        </Stack>
      }
      filters={filters}
    />
  );
};
